/**
 * Shared constants for PathGR application
 */

// API Routes
export const API_ROUTES = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    REFRESH: '/auth/refresh',
    VERIFY_EMAIL: '/auth/verify-email',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    PROFILE: '/auth/profile'
  },
  USERS: {
    BASE: '/users',
    PROFILE: '/users/profile',
    FOLLOW: '/users/follow',
    UNFOLLOW: '/users/unfollow',
    FOLLOWERS: '/users/followers',
    FOLLOWING: '/users/following'
  },
  CASES: {
    BASE: '/cases',
    UPLOAD_IMAGE: '/cases/upload-image',
    RESOLVE: '/cases/:id/resolve',
    COMMENTS: '/cases/:id/comments',
    VOTE: '/cases/:id/vote'
  },
  FORUM: {
    CATEGORIES: '/forum/categories',
    POSTS: '/forum/posts',
    REPLIES: '/forum/replies'
  },
  ADMIN: {
    USERS: '/admin/users',
    CASES: '/admin/cases',
    FORUM: '/admin/forum',
    ACTIONS: '/admin/actions'
  },
  NOTIFICATIONS: '/notifications'
} as const;

// File Upload Limits
export const FILE_LIMITS = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_FILES_PER_CASE: 10,
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  ALLOWED_EXTENSIONS: ['.jpg', '.jpeg', '.png', '.gif', '.webp']
} as const;

// Pagination Defaults
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  CASES_PER_PAGE: 12,
  COMMENTS_PER_PAGE: 10,
  FORUM_POSTS_PER_PAGE: 15,
  NOTIFICATIONS_PER_PAGE: 20
} as const;

// Cache Durations (in seconds)
export const CACHE_DURATION = {
  SHORT: 300, // 5 minutes
  MEDIUM: 1800, // 30 minutes
  LONG: 3600, // 1 hour
  VERY_LONG: 86400 // 24 hours
} as const;

// JWT Configuration
export const JWT_CONFIG = {
  ACCESS_TOKEN_EXPIRE: '15m',
  REFRESH_TOKEN_EXPIRE: '7d',
  ALGORITHM: 'HS256'
} as const;

// Rate Limiting
export const RATE_LIMITS = {
  AUTH: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    max: 5 // 5 attempts per window
  },
  GENERAL: {
    windowMs: 15 * 60 * 1000, // 15 minutes
    max: 100 // 100 requests per window
  },
  UPLOAD: {
    windowMs: 60 * 1000, // 1 minute
    max: 5 // 5 uploads per minute
  }
} as const;

// Case Types (Greek pathology specialties)
export const CASE_TYPES = [
  'Ιστοπαθολογία',
  'Κυτταρολογία',
  'Αυτοψία',
  'Παγωμένες Τομές',
  'Ανοσοιστοχημεία',
  'Μοριακή Παθολογική Ανατομική',
  'Ηλεκτρονική Μικροσκοπία',
  'Τηλεπαθολογία'
] as const;

// Staining Methods
export const STAINING_METHODS = [
  'H&E',
  'PAS',
  'Masson Trichrome',
  'Van Gieson',
  'Congo Red',
  'Reticulin',
  'Prussian Blue',
  'Alcian Blue',
  'Immunohistochemistry',
  'Special Stains'
] as const;

// Tissue Types
export const TISSUE_TYPES = [
  'Δέρμα',
  'Πνεύμονας',
  'Γαστρεντερικό',
  'Γεννητουρητικό',
  'Λεμφικό',
  'Ενδοκρινικό',
  'ΚΝΣ',
  'Μυοσκελετικό',
  'Καρδιαγγειακό',
  'Οφθαλμός',
  'ΩΡΛ',
  'Μαστός',
  'Άλλο'
] as const;


// Areas of Interest (updated subspecialties)
export const AREAS_OF_INTEREST = [
  'Οστά και μαλακά μόρια',
  'Θωρακική κοιλότητα',
  'Δέρμα',
  'Παιδιατρικό',
  'Παθ. ανατομική του οφθαλμού',
  'Αιμολεμφικό',
  'Κεφαλή & Τράχηλος',
  'Γυναικολογικό',
  'Ουρογεννητικό',
  'Γαστρεντερικό',
  'Ενδοκρινικό και Νευροενδοκρινικό',
  'Κεντρικό Νευρικό σύστημα',
  'Μαστός',
  'Κυτταρολογία',
  'Μοριακή Παθ. Ανατομική',
  'Νεκροτομές'
] as const;

// Validation Rules
export const VALIDATION = {
  PASSWORD: {
    MIN_LENGTH: 8,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBERS: true,
    REQUIRE_SPECIAL: true
  },
  EMAIL: {
    MAX_LENGTH: 255
  },
  TEXT: {
    TITLE_MAX_LENGTH: 200,
    DESCRIPTION_MAX_LENGTH: 5000,
    COMMENT_MAX_LENGTH: 2000,
    BIO_MAX_LENGTH: 500
  },
  NAME: {
    MIN_LENGTH: 2,
    MAX_LENGTH: 100
  }
} as const;

// Error Messages (Greek)
export const ERROR_MESSAGES = {
  AUTH: {
    INVALID_CREDENTIALS: 'Λάθος email ή κωδικός πρόσβασης',
    EMAIL_EXISTS: 'Το email υπάρχει ήδη',
    WEAK_PASSWORD: 'Ο κωδικός πρόσβασης είναι πολύ αδύναμος',
    INVALID_TOKEN: 'Μη έγκυρο token',
    TOKEN_EXPIRED: 'Το token έχει λήξει',
    UNAUTHORIZED: 'Δεν έχετε δικαίωμα πρόσβασης',
    FORBIDDEN: 'Απαγορεύεται η πρόσβαση'
  },
  VALIDATION: {
    REQUIRED: 'Το πεδίο είναι υποχρεωτικό',
    INVALID_EMAIL: 'Μη έγκυρο email',
    INVALID_FORMAT: 'Μη έγκυρη μορφή',
    TOO_SHORT: 'Πολύ μικρό',
    TOO_LONG: 'Πολύ μεγάλο'
  },
  FILE: {
    TOO_LARGE: 'Το αρχείο είναι πολύ μεγάλο',
    INVALID_TYPE: 'Μη έγκυρος τύπος αρχείου',
    UPLOAD_FAILED: 'Η μεταφόρτωση απέτυχε'
  },
  GENERAL: {
    NOT_FOUND: 'Δε βρέθηκε',
    SERVER_ERROR: 'Σφάλμα διακομιστή',
    NETWORK_ERROR: 'Σφάλμα δικτύου'
  }
} as const;

// Success Messages (Greek)
export const SUCCESS_MESSAGES = {
  AUTH: {
    LOGIN_SUCCESS: 'Επιτυχής σύνδεση',
    REGISTER_SUCCESS: 'Επιτυχής εγγραφή',
    LOGOUT_SUCCESS: 'Επιτυχής αποσύνδεση',
    PASSWORD_RESET: 'Ο κωδικός επαναφέρθηκε επιτυχώς'
  },
  CASE: {
    CREATED: 'Η περίπτωση δημιουργήθηκε επιτυχώς',
    UPDATED: 'Η περίπτωση ενημερώθηκε επιτυχώς',
    RESOLVED: 'Η περίπτωση επιλύθηκε επιτυχώς',
    DELETED: 'Η περίπτωση διαγράφηκε επιτυχώς'
  },
  COMMENT: {
    CREATED: 'Το σχόλιο προστέθηκε επιτυχώς',
    UPDATED: 'Το σχόλιο ενημερώθηκε επιτυχώς',
    DELETED: 'Το σχόλιο διαγράφηκε επιτυχώς'
  },
  GENERAL: {
    SAVED: 'Αποθηκεύτηκε επιτυχώς',
    UPDATED: 'Ενημερώθηκε επιτυχώς',
    DELETED: 'Διαγράφηκε επιτυχώς'
  }
} as const;